// ============================================================================
// BP-13-02: Admin User Types
// BrightPath Platform - Admin Frontend
// ============================================================================

import { z } from 'zod';

// =============================================================================
// Enums
// =============================================================================

export const UserStatusEnum = z.enum([
  'active',
  'suspended',
  'pending_verification',
  'deactivated',
]);
export type UserStatus = z.infer<typeof UserStatusEnum>;

export const UserRoleEnum = z.enum([
  'family_admin',
  'family_member',
  'caregiver',
  'attorney',
  'vendor',
  'provider',
  'admin',
  'super_admin',
]);
export type UserRole = z.infer<typeof UserRoleEnum>;

export const SuspensionReasonEnum = z.enum([
  'terms_violation',
  'spam',
  'inappropriate_content',
  'fraud',
  'other',
]);
export type SuspensionReason = z.infer<typeof SuspensionReasonEnum>;

// =============================================================================
// User Schemas
// =============================================================================

export const AdminUserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  firstName: z.string(),
  lastName: z.string(),
  fullName: z.string(),
  avatarUrl: z.string().nullable(),
  role: UserRoleEnum,
  status: UserStatusEnum,
  emailVerified: z.boolean(),
  emailVerifiedAt: z.string().datetime().nullable(),
  phoneNumber: z.string().nullable(),
  phoneVerified: z.boolean(),
  lastLoginAt: z.string().datetime().nullable(),
  lastActiveAt: z.string().datetime().nullable(),
  loginCount: z.number(),
  failedLoginAttempts: z.number(),
  lockedUntil: z.string().datetime().nullable(),
  mfaEnabled: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  suspendedAt: z.string().datetime().nullable(),
  suspensionReason: SuspensionReasonEnum.nullable(),
  suspensionNotes: z.string().nullable(),
  metadata: z.record(z.unknown()).nullable(),
});
export type AdminUser = z.infer<typeof AdminUserSchema>;

export const AdminUserListItemSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  fullName: z.string(),
  avatarUrl: z.string().nullable(),
  role: UserRoleEnum,
  status: UserStatusEnum,
  emailVerified: z.boolean(),
  lastLoginAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  familyCount: z.number().optional(),
  childCount: z.number().optional(),
});
export type AdminUserListItem = z.infer<typeof AdminUserListItemSchema>;

// =============================================================================
// User Activity
// =============================================================================

export const UserActivityTypeEnum = z.enum([
  'login',
  'logout',
  'password_change',
  'profile_update',
  'document_upload',
  'health_log_entry',
  'appointment_created',
  'forum_post',
  'review_submitted',
  'expense_added',
  'other',
]);
export type UserActivityType = z.infer<typeof UserActivityTypeEnum>;

export const UserActivitySchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  activityType: UserActivityTypeEnum,
  description: z.string(),
  ipAddress: z.string().nullable(),
  userAgent: z.string().nullable(),
  metadata: z.record(z.unknown()).nullable(),
  createdAt: z.string().datetime(),
});
export type UserActivity = z.infer<typeof UserActivitySchema>;

// =============================================================================
// Request/Response Schemas
// =============================================================================

export const AdminUserListParamsSchema = z.object({
  page: z.number().min(1).default(1),
  pageSize: z.number().min(1).max(100).default(20),
  search: z.string().optional(),
  role: UserRoleEnum.optional(),
  status: UserStatusEnum.optional(),
  sortBy: z.enum(['createdAt', 'lastLoginAt', 'email', 'fullName']).default('createdAt'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
  emailVerified: z.boolean().optional(),
  dateFrom: z.string().datetime().optional(),
  dateTo: z.string().datetime().optional(),
});
export type AdminUserListParams = z.infer<typeof AdminUserListParamsSchema>;

export const AdminUserListResponseSchema = z.object({
  items: z.array(AdminUserListItemSchema),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
  totalPages: z.number(),
});
export type AdminUserListResponse = z.infer<typeof AdminUserListResponseSchema>;

export const UpdateAdminUserSchema = z.object({
  firstName: z.string().min(1).optional(),
  lastName: z.string().min(1).optional(),
  email: z.string().email().optional(),
  role: UserRoleEnum.optional(),
  phoneNumber: z.string().optional(),
});
export type UpdateAdminUser = z.infer<typeof UpdateAdminUserSchema>;

export const SuspendUserRequestSchema = z.object({
  reason: SuspensionReasonEnum,
  notes: z.string().optional(),
  duration: z.enum(['1_day', '7_days', '30_days', 'permanent']).optional(),
});
export type SuspendUserRequest = z.infer<typeof SuspendUserRequestSchema>;

export const UserActivityListParamsSchema = z.object({
  page: z.number().min(1).default(1),
  pageSize: z.number().min(1).max(100).default(20),
  activityType: UserActivityTypeEnum.optional(),
  dateFrom: z.string().datetime().optional(),
  dateTo: z.string().datetime().optional(),
});
export type UserActivityListParams = z.infer<typeof UserActivityListParamsSchema>;

export const UserActivityListResponseSchema = z.object({
  items: z.array(UserActivitySchema),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
  totalPages: z.number(),
});
export type UserActivityListResponse = z.infer<typeof UserActivityListResponseSchema>;

// =============================================================================
// User Statistics
// =============================================================================

export const UserStatsSchema = z.object({
  totalUsers: z.number(),
  activeUsers: z.number(),
  suspendedUsers: z.number(),
  pendingVerification: z.number(),
  newUsersToday: z.number(),
  newUsersThisWeek: z.number(),
  newUsersThisMonth: z.number(),
  activeUsersToday: z.number(),
  usersByRole: z.record(UserRoleEnum, z.number()),
  usersByStatus: z.record(UserStatusEnum, z.number()),
});
export type UserStats = z.infer<typeof UserStatsSchema>;
